// ────────────────────────────────────────────────────────────────────
// NotesDock — the floating per-focus scratchpad (workbench roadmap C3).
// Floating, not tiled: "tiled = things you stare at all day, floating =
// things you pick up and put down" (roadmap C1). Bound to the shared
// workspace focus (asset/cml today; plant/area whenever those screens set
// one) — switch focus, the note switches with it, same idiom as MODS's
// asset-focus picker.
//
// STAFF-ONLY: server/routers/notes.py gates both GET and PUT with
// STAFF_ROLES (these are internal working notes, "scratch paper" — a
// customer must never see one). Mirrored here at the MOUNT level (see
// NotesDockMounter / AppShell), not just inside the body, so a non-staff
// session never even registers the window — "hidden for non-staff" per
// spec, not merely an empty state.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StickyNote } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useWorkspaceFocus } from "./WorkspaceContext";
import { useDock } from "./dock";

type SaveStatus = "idle" | "saving" | "saved" | "error";

// A component reference kept stable across renders (module-level function,
// no dynamic identity) — same reasoning as ModsBody in ChatDock.tsx.
export function NotesBody(): JSX.Element {
  const { focus } = useWorkspaceFocus();
  const hasFocus = !!focus && !!focus.type;
  const focusKey = hasFocus ? `${focus!.type}:${focus!.ref}` : null;

  const noteQ = useQuery({
    queryKey: ["focus-note", focus?.type, focus?.ref],
    queryFn: () => apiClient.focusNote(focus!.type as string, focus!.ref),
    enabled: hasFocus,
  });

  const [text, setText] = useState("");
  const [status, setStatus] = useState<SaveStatus>("idle");
  // The last value actually persisted (or loaded) — the autosave effect
  // diffs against this, not against the previous render's text, so it never
  // re-saves an unchanged note just because the component re-rendered.
  const lastSavedRef = useRef<string>("");

  // Load this focus's note into the textarea. Keyed on focusKey (not just
  // noteQ.data) so a genuinely-empty note on a NEW focus still resets the
  // box — otherwise an empty body === "" would look identical to "nothing
  // has loaded yet" and the previous focus's text would linger.
  useEffect(() => {
    if (!hasFocus) { setText(""); lastSavedRef.current = ""; setStatus("idle"); return; }
    if (noteQ.data && focusKey) {
      setText(noteQ.data.body);
      lastSavedRef.current = noteQ.data.body;
      setStatus("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, noteQ.data]);

  // Autosave — debounced 800ms after the last keystroke.
  useEffect(() => {
    if (!hasFocus || text === lastSavedRef.current) return;
    setStatus("saving");
    const h = setTimeout(() => {
      apiClient.saveFocusNote(focus!.type as string, focus!.ref, text)
        .then(() => { lastSavedRef.current = text; setStatus("saved"); })
        .catch(() => setStatus("error"));
    }, 800);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, focusKey]);

  if (!hasFocus) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 text-center font-mono"
           style={{ fontSize: 11, color: "var(--ink-3)" }}>
        เลือก asset หรือ CML เพื่อจดโน้ต
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-3 gap-2 min-h-0">
      <div className="flex items-center justify-between shrink-0">
        <span className="font-mono uppercase truncate" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-4)" }}>
          {focus!.label || focus!.ref}
        </span>
        <span className="font-mono shrink-0" style={{ fontSize: 9, color: status === "error" ? "var(--err)" : "var(--ink-4)" }}>
          {status === "saving" ? "กำลังบันทึก…" : status === "saved" ? "บันทึกแล้ว" : status === "error" ? "บันทึกไม่สำเร็จ" : ""}
        </span>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)}
                placeholder="จดบันทึกส่วนตัว — ไม่มีใครเห็นนอกจากทีม"
                aria-label="โน้ต"
                className="flex-1 w-full resize-none font-mono px-2 py-1.5"
                style={{ fontSize: 12, lineHeight: 1.5, color: "var(--ink-1)", background: "var(--paper)", border: "1px solid var(--hairline)" }} />
    </div>
  );
}

// NotesDockMounter — registers the notes window with the shared dock.
// Mount ONLY for staff (see AppShell: `{isStaff && <NotesDockMounter />}`)
// so the window never exists at all for a customer session.
export function NotesDockMounter(): JSX.Element | null {
  const { registerWindow, unregisterWindow } = useDock();
  useEffect(() => {
    registerWindow("notes", {
      title: "โน้ต",
      icon: <StickyNote size={14} className="text-brand" />,
      body: <NotesBody />,
      defaultOpen: false,
    });
    return () => unregisterWindow("notes");
  }, [registerWindow, unregisterWindow]);
  return null;
}
